import { Fragment, useMemo } from 'react';
import { BigText, Content, Stack, Strong, Text } from '@kinesis/bungle';
import { DisclosureOverviewPage, SourceType } from '@/types';
import { renderActivity, renderSourceType } from '@/data/labels';
import { renderShare, renderUnit, renderValue } from '@/data/units';
import { sourceColors } from '@/data/colors';
import { ShareBar } from '@/components/share-bar';
import { DisclosureTreemap } from '@/components/disclosure-treemap';
import {
  DisclosureTable,
  Cell,
} from '@/components/disclosure-table/disclosure-table.styles';

// Renders the Community disclosure's Overview page: the headline total, the
// "by source" breakdown table (one row per source with its CO₂-e, share of
// the grand total as % + a coloured bar, ranked largest-first by the server),
// and the "summary by source" treemap (Source → Activity, sized by CO₂-e).
// The emissions `unit` is server-picked at page scope; `share` is
// server-computed — the FE only formats it and sizes the bar/boxes, it
// derives nothing. Colour is the source-type palette; treemap leaves are
// auto-shaded tints of their source. Store-less per the component/page split;
// the page owns the RTK call.
const DisclosureOverview = (props: DisclosureOverviewProps) => {
  const { pageData, onSelectSource, showActivityBreakdown = false } = props;
  const { unit, total, bySource } = pageData;
  // Map the payload into the treemap's shape — label + palette lookup only;
  // the activity totals come straight from the server. Memoised so the
  // treemap's `options` (and thus the chart) only rebuild when `bySource`
  // actually changes, not on every incidental page re-render.
  const treemapGroups = useMemo(
    () =>
      bySource.map((source) => ({
        key: source.sourceType,
        label: renderSourceType(source.sourceType),
        color: sourceColors[source.sourceType],
        children: source.activities.map((activity) => ({
          key: activity.activity,
          label: renderActivity(activity.activity),
          value: activity.total,
        })),
      })),
    [bySource],
  );
  // The treemap reports the clicked GROUP key, which on the Overview is the
  // source type — narrow it and deep-link to that source's page. Memoised on
  // the (already-stable) `onSelectSource` so the adapter closure doesn't churn
  // the treemap's options each render.
  const handleSelect = useMemo(
    () =>
      onSelectSource
        ? (key: string) => onSelectSource(key as SourceType)
        : undefined,
    [onSelectSource],
  );
  return (
    <Stack space='medium'>
      <Stack space='small'>
        <Text tone='secondary'>Total emissions</Text>
        <BigText>{renderValue(total, unit)}</BigText>
        <Text tone='secondary'>{renderUnit(unit)}</Text>
      </Stack>
      {bySource.length > 0 && (
        <Stack space='small'>
          <Strong>Emissions by source</Strong>
          <Content
            background='white'
            padding='medium'
            border
            paddingMode='equal'
            borderRadiusSize='small'
          >
            <DisclosureTable $columns='minmax(0, 1fr) minmax(96px, 0.5fr) auto auto'>
              <Cell $heading>Source</Cell>
              <Cell $heading aria-hidden='true' />
              <Cell $heading $align='right'>
                {renderUnit(unit)}
              </Cell>
              <Cell $heading $align='right'>
                %
              </Cell>
              {bySource.map((source) => (
                <Fragment key={source.sourceType}>
                  <Cell>{renderSourceType(source.sourceType)}</Cell>
                  <Cell>
                    <ShareBar
                      share={source.share}
                      color={sourceColors[source.sourceType]}
                    />
                  </Cell>
                  <Cell $align='right'>{renderValue(source.total, unit)}</Cell>
                  <Cell $align='right'>{renderShare(source.share)}</Cell>
                  {/* One level deeper: the source's activities, indented
                      under it with the same magnitude line (bar in the
                      source colour + value + %). Rendered in the server's
                      order — the FE adds no sorting. Gated by the
                      `community.disclosure-depth` flag. */}
                  {showActivityBreakdown &&
                    source.activities.map((activity) => (
                      <Fragment key={activity.activity}>
                        <Cell $indent>{renderActivity(activity.activity)}</Cell>
                        <Cell>
                          <ShareBar
                            share={activity.share}
                            color={sourceColors[source.sourceType]}
                          />
                        </Cell>
                        <Cell $align='right'>
                          {renderValue(activity.total, unit)}
                        </Cell>
                        <Cell $align='right'>
                          {renderShare(activity.share)}
                        </Cell>
                      </Fragment>
                    ))}
                </Fragment>
              ))}
              <Cell $heading>Total</Cell>
              <Cell $heading aria-hidden='true' />
              <Cell $heading $align='right'>
                {renderValue(total, unit)}
              </Cell>
              <Cell $heading $align='right'>
                {renderShare(1)}
              </Cell>
            </DisclosureTable>
          </Content>
        </Stack>
      )}
      {bySource.length > 0 && (
        <Stack space='small'>
          <Strong>Summary by source</Strong>
          <Content
            background='white'
            padding='medium'
            border
            paddingMode='equal'
            borderRadiusSize='small'
          >
            <DisclosureTreemap
              groups={treemapGroups}
              unit={unit}
              onSelect={handleSelect}
            />
          </Content>
        </Stack>
      )}
    </Stack>
  );
};

export type DisclosureOverviewProps = {
  pageData: DisclosureOverviewPage;
  // When provided, clicking a treemap box navigates to that source's page.
  // The page owns navigation (component/page split); this is store-less.
  onSelectSource?: (sourceType: SourceType) => void;
  // When true (the `community.disclosure-depth` flag), each source row in the
  // by-source table expands to show its activities one level deeper —
  // indented, same magnitude line. Default false: just the source rows.
  showActivityBreakdown?: boolean;
};

export { DisclosureOverview };
